import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    DataGridPro, 
} from '@mui/x-data-grid-pro';
import {
    CircularProgress,
    Box,
} from '@mui/material';
import axios from 'axios';
import { APIURL } from '../../config';
import SaveViewButton from './SaveViewButton';
import DefaultToolbar from './DefaultToolbar';
//import LoadingSkeleton from './LoadingSkeleton';


// This is a custom DataGrid component that allows for the user to save their grid settings
// and have them persist between sessions. It also allows for the user to save their own
// custom views of the grid
// mandatory props:
//      gridId: the id of the grid. This is used to save the grid settings to the database
//      user: the user object
//      columns: the columns of the grid
//      rows: the rows of the grid
//      apiRef: the apiRef of the grid

// Besides the mandatory props, you can use this component just like you would use the DataGridPro component

// If you want to use a Custom Toolbar you can pass it as a function that returns the toolbar along
// with the SaveViewButton component. The function should take in params and spread them into the 
// SaveViewButton component like in the DefaultToolbar function below

// optional props:
//      onLoad: a function or array of functions that will be called when the grid loads. The function(s) will be passed the settings object
//      onSave: a function or array of functions that will be called when the user saves the grid settings. The function(s) will be passed the settings object

const ControlledDataGrid = (props) => {
    const [isLoadingSettings, setIsLoadingSettings] = useState(true);
    const [initialSettings, setInitialSettings] = useState(null);
    const [orderedColumns, setOrderedColumns] = useState(props.columns ? props.columns.reduce((acc, col) => ([...acc, col.field]), []) : []);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(props.columnVisibilityModel ?? {});
    const [density, setDensity] = useState("standard");
    const [sortModel, setSortModel] = useState([]);
    const [rows, setRows] = useState(props.rows ? props.rows : []);
    const isDataSourceMode = !!props.dataSource;

    useEffect(() => {
        const fetchSettings = async () => {
            // Check if this is a custom report (negative gridId)
            const isCustomReport = props.gridId < 0;
            const customReportId = isCustomReport ? Math.abs(props.gridId) : null;

            if (isCustomReport) {
                // Use custom report settings endpoint
                console.log('ControlledDataGrid - Fetching custom report settings for report ID:', customReportId);
                let getGridSettings = await axios.get(`${APIURL}/reports/custom/${customReportId}/settings`, { 
                    params: { user_id: props.user.emp_id } 
                });
                
                if (getGridSettings.data.settings) {
                    let settings = getGridSettings.data.settings;

                    if (settings.columns?.orderedFields) {
                        const extraColumns = props.columns
                            .map((col) => col.field)
                            .filter((field) => !settings.columns.orderedFields.includes(field));
                        settings.columns.orderedFields = [
                            settings.columns.orderedFields[0], 
                            ...extraColumns,                  
                            ...settings.columns.orderedFields.slice(1), 
                        ];
                    }

                    setInitialSettings({ ...props.initialState, ...settings }); 
                    setColumnVisibilityModel({ ...props.columnVisibilityModel, ...settings.columnVisibilityModel });                
                    setDensity(settings.density ? settings.density : "standard");
                    setSortModel(settings.sortModel ? settings.sortModel : []);
                    setOrderedColumns(settings.columns ? settings.columns.orderedFields : []);
                    if (props.onLoad) {
                        Array.isArray(props.onLoad) ? props.onLoad.forEach((func) => func(settings)) : props.onLoad(settings);
                    }
                } else {
                    console.log('ControlledDataGrid - No existing settings for custom report:', customReportId);
                    let settings = { 
                        ...props.initialState,
                        columns: {
                            orderedFields: props.columns.map(col => col.field)
                        }
                    };
                    setInitialSettings(settings);
                }
            } else {
                // Use regular datagrid endpoint
                let getGridSettings = await axios.get(`${APIURL}/datagrid`, { params: { user_id: props.user.emp_id, report_id: props.gridId } });
                if (getGridSettings.data.length > 0) {
                    let settings = JSON.parse(getGridSettings.data[0].settings);

                    if (settings.columns?.orderedFields) {
                        const extraColumns = props.columns
                            .map((col) => col.field)
                            .filter((field) => !settings.columns.orderedFields.includes(field));
                        settings.columns.orderedFields = [
                            settings.columns.orderedFields[0], 
                            ...extraColumns,                  
                            ...settings.columns.orderedFields.slice(1), 
                        ];
                    }

                    setInitialSettings({ ...props.initialState, ...settings }); 
                    setColumnVisibilityModel({ ...props.columnVisibilityModel, ...settings.columnVisibilityModel });                
                    setDensity(settings.density ? settings.density : "standard");
                    setSortModel(settings.sortModel ? settings.sortModel : []);
                    setOrderedColumns(settings.columns ? settings.columns.orderedFields : []);
                    if (props.onLoad) {
                        Array.isArray(props.onLoad) ? props.onLoad.forEach((func) => func(settings)) : props.onLoad(settings);
                    }
                } else {
                    console.log('ControlledDataGrid - No existing settings, creating initial record for gridId:', props.gridId);
                    let settings = { 
                        ...props.initialState,
                        columns: {
                            orderedFields: props.columns.map(col => col.field)
                        }
                    };
                    setInitialSettings(settings);
                    
                    const settingsString = JSON.stringify(settings);          
                    let saveGridSettings = await axios.post(`${APIURL}/datagrid`, { user_id: props.user.emp_id, report_id: props.gridId, settings: settingsString });
                    console.log('ControlledDataGrid - Initial POST response:', saveGridSettings.data);
                }
            }
        }
        fetchSettings().then(() => {
            setIsLoadingSettings(false);
        }).catch((error) => {
            console.error('Error fetching grid settings:', error);
            setIsLoadingSettings(false);
        });
    }, []);

    useEffect(() => {
        if (initialSettings !== null) {
            setIsLoadingSettings(false);
        }
    }, [initialSettings]);

    useEffect(() => {
        if (!isDataSourceMode && props.rows) {
            setRows(props.rows);
        }
    }, [props.rows, isDataSourceMode]);

    const handleColumnOrderChange = useCallback((params) => {
        setOrderedColumns((prevOrderedColumns) => {
          if (!prevOrderedColumns || !Array.isArray(prevOrderedColumns)) {
            return [];
          }
          const newOrderedColumns = [...prevOrderedColumns];
          const offset = props.checkboxSelection ? 1 : 0;
          const oldIndex = params.oldIndex - offset;
          const targetIndex = params.targetIndex - offset;
          const oldColumn = prevOrderedColumns[oldIndex];
          newOrderedColumns.splice(oldIndex, 1);
          newOrderedColumns.splice(targetIndex, 0, oldColumn);
          return newOrderedColumns;
        });        
      }, [props.checkboxSelection]);

    const handleColumnVisibilityModelChange = (newModel) => {
        setColumnVisibilityModel(newModel);
    }

    const columns = useMemo(() => {
        if (!props.columns) return [];
        
        // Create a map of all columns by field
        let columnsMap = props.columns.reduce((acc, col) => ({ ...acc, [col.field]: col }), {});
        
        // Apply saved column widths
        if (initialSettings?.columns?.dimensions) {
            Object.keys(initialSettings.columns.dimensions).forEach((field) => {
                if (columnsMap[field]) {
                    columnsMap[field] = {
                        ...columnsMap[field],
                        width: initialSettings.columns.dimensions[field].width
                    };
                }
            });
        }

        // If we have saved column order, use it
        if (initialSettings?.columns?.orderedFields && Array.isArray(initialSettings.columns.orderedFields)) {
            const orderedCols = [];
            const usedFields = new Set();
            
            // Add columns in saved order
            initialSettings.columns.orderedFields.forEach(field => {
                if (columnsMap[field]) {
                    orderedCols.push(columnsMap[field]);
                    usedFields.add(field);
                }
            });
            
            // Add any new columns that weren't in the saved order
            props.columns.forEach(col => {
                if (!usedFields.has(col.field)) {
                    orderedCols.push(columnsMap[col.field]);
                }
            });
            
            return orderedCols;
        }
        
        // No saved order, return columns as-is
        return props.columns.map(col => columnsMap[col.field]);
    }, [props.columns, initialSettings]);

    useEffect(() => {
        console.log('columns', columns);
        
    }, [columns]);

    const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), wait);
        };
    };

    const handleSaveSettings = async () => {
        let settings = props.apiRef.current.exportState();
        settings.columnVisibilityModel = columnVisibilityModel;
        settings.density = density;
        settings.sortModel = sortModel;

        console.log('ControlledDataGrid - Saving settings for gridId:', props.gridId, 'user_id:', props.user.emp_id);

        if(props.onSave) {
            console.log('Saving settings:', settings);
            
            Array.isArray(props.onSave) ? props.onSave.forEach((func) => func(settings)) : props.onSave(settings);
        }

        // Check if this is a custom report (negative gridId)
        const isCustomReport = props.gridId < 0;
        const customReportId = isCustomReport ? Math.abs(props.gridId) : null;

        if (isCustomReport) {
            // Use custom report settings endpoint
            console.log('ControlledDataGrid - Saving custom report settings for report ID:', customReportId);
            let saveGridSettings = await axios.post(`${APIURL}/reports/custom/settings`, { 
                id: customReportId,
                user_id: props.user.emp_id, 
                settings: settings 
            });
            console.log('ControlledDataGrid - Save response:', saveGridSettings.data);
        } else {
            // Use regular datagrid endpoint
            const serializedSettings = JSON.stringify(settings, (key, value) =>
                value instanceof Set ? [...value] : value
            );
            let saveGridSettings = await axios.put(`${APIURL}/datagrid`, { user_id: props.user.emp_id, report_id: props.gridId, settings: serializedSettings });
            console.log('ControlledDataGrid - Save response:', saveGridSettings.data);
        }
    }
    

    return (<>
            <DataGridPro
                {...props}
                sx={{ 
                    backgroundColor: '#F9F9F9',
                    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell, & .MuiDataGrid-filler--pinnedLeft': {
                        backgroundColor: '#F9F9F9',
                    },
                    '& .MuiTablePagination-root': {
                        marginRight: '48px',
                    },
                    ...props.sx 
                }}
                rows={rows}
                columns={columns}
                density={density}
                onDensityChange={(newDensity) => setDensity(newDensity)}
                initialState={{ ...props.initialState, ...initialSettings }}
                slots={{
                    color: '#3fadf6',
                    ...props.components,
                    ...props.slots,
                    toolbar: DefaultToolbar,
                }}
                slotProps={{
                    loadingOverlay: props.slotProps?.loadingOverlay,
                    toolbar: {
                        children: (
                            <>
                                {(Array.isArray(props.toolbarButtons) ? props.toolbarButtons : []).map((Btn, idx) => (
                                    <Btn key={idx} />
                                ))}
                                <SaveViewButton onClick={debounce(handleSaveSettings, 500)} />
                            </>
                        ),
                    },
                }}
                onColumnOrderChange={handleColumnOrderChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                sortModel={sortModel}
                onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                showToolbar={props.showToolbar !== undefined ? props.showToolbar : true}
            />
    </>);
}

export default ControlledDataGrid;